import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { DialogWrapper } from '../display-elements-components/dialog-wrapper'
import { AvatarDisplay } from '../display-elements-components/avatar-display'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import ReadinessService from '../../../shared/services/readiness/readiness.service'
import UserDetailsService from '../../../shared/services/user-details/user-details.service'
import { IReadinessFinding } from '../../../shared/services/readiness/readiness.model'
import { IUserDetails } from '../../../shared/services/user-details/user-details.model'

// notifyAssignee: email the assignee about the task (ignored when unassigned).
export type ICreateTaskFromFindingPayload = { checkId: string, entityKey: string, userId: string | null, notifyAssignee: boolean }

// The fields the assignee list needs; the client page passes full user details, the partner
// console the client users the drill-in returned.
export type IAssignableUser = Pick<IUserDetails, 'id' | 'firstName' | 'surname' | 'avatarColor'>

// Turn a finding into an owned task, optionally assigning it in the same step. Findings route
// to people through tasks: the task carries the checkId, and the finding clears because the
// data changes, not because anyone ticked anything.
export const CreateTaskFromFindingDialog: FunctionComponent<{
  open: boolean,
  finding: IReadinessFinding | null,
  reportPeriodId: string,
  closeHandler: () => void,
  onCreated: () => void,
  /** Assignee list to offer instead of fetching the session organisation's users (the partner
   * console passes the client's users). */
  users?: IAssignableUser[],
  /** Where to post the task instead of the session organisation's readiness endpoint. */
  submit?: (payload: ICreateTaskFromFindingPayload) => Promise<unknown>,
  /** Success snackbar text override (e.g. "Task created in Oakfield Ltd"). */
  successMessage?: string,
}> = ({
  open,
  finding,
  reportPeriodId,
  closeHandler,
  onCreated,
  users: providedUsers,
  submit,
  successMessage,
}) => {

    const userNotificationsContext = useContext(UserNotificationsContext)
    const readinessService = new ReadinessService()
    const [fetchedUsers, setFetchedUsers] = useState<IAssignableUser[]>([])
    const users = providedUsers ?? fetchedUsers
    const [userId, setUserId] = useState('unassigned')
    const [notifyAssignee, setNotifyAssignee] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
      if (open) {
        setUserId('unassigned')
        setNotifyAssignee(true)
        setIsSubmitting(false)
      }
    }, [open, finding?.checkId, finding?.entityKey])

    useEffect(() => {
      if (!open || providedUsers || fetchedUsers.length > 0) return
      new UserDetailsService().getAllUserDetails(`?status=active`)
        .then((response) => {
          setFetchedUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => console.log(err))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const submitHandler = () => {
      if (!finding) return
      setIsSubmitting(true)
      const assignee = users.find(user => user.id === userId)
      const payload: ICreateTaskFromFindingPayload = {
        checkId: finding.checkId,
        entityKey: finding.entityKey,
        userId: userId === 'unassigned' ? null : userId,
        notifyAssignee: userId !== 'unassigned' && notifyAssignee,
      }
      const request = submit ? submit(payload) : readinessService.createTaskFromFinding(reportPeriodId, payload)
      request
        .then(() => {
          const defaultMessage = assignee
            ? `Task created and assigned to ${assignee.firstName} ${assignee.surname}. Find it under the Tasks tab.`
            : 'Task created. Find it under the Tasks tab.'
          userNotificationsContext.openSnackbar({
            open: true,
            message: successMessage
              ? (assignee ? `${successMessage}, assigned to ${assignee.firstName} ${assignee.surname}.` : `${successMessage}.`)
              : defaultMessage,
          })
          closeHandler()
          onCreated()
        })
        .catch(err => {
          const message = err?.response?.data?.message || 'There was an error, please try again'
          userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        })
        .finally(() => setIsSubmitting(false))
    }

    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title='Create task'
        cancelHandler={closeHandler}
        submitHandler={submitHandler}
        isSubmitting={isSubmitting}
        submitButtonText='Create'
        maxWidth='sm'
      >
        <Stack spacing={2}>
          {finding && <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{finding.title}</Typography>}
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {finding?.taskTitle
              ? <>Creates the task "{finding.taskTitle}" under the Tasks tab. The finding itself stays open until the data changes.</>
              : 'This creates an owned task for the finding under the Tasks tab. The finding itself stays open until the data changes.'}
          </Typography>
          <TextField
            select
            label='Assign to (optional)'
            fullWidth
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <MenuItem value='unassigned'>
              <Stack display='flex' direction='row' spacing={2} alignItems='center'>
                <AvatarDisplay color={'#e7e7e7'} width={'25px'} height={'25px'} padding={0} fontSize={'0.75rem'} />
                <Typography>Unassigned</Typography>
              </Stack>
            </MenuItem>
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>
                <Stack display='flex' direction='row' spacing={2} alignItems='center'>
                  <AvatarDisplay color={user.avatarColor} width={'25px'} height={'25px'} padding={0} userDetails={user as IUserDetails} fontSize={'0.75rem'} />
                  <Typography>{user.firstName} {user.surname}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
          {userId !== 'unassigned' &&
            <FormControlLabel
              control={<Checkbox checked={notifyAssignee} onChange={(e) => setNotifyAssignee(e.target.checked)} />}
              label={<Typography variant='body2'>Email {users.find(user => user.id === userId)?.firstName || 'them'} about this task</Typography>}
            />}
        </Stack>
      </DialogWrapper>
    )
  }
